import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function WorkRelationshipDecodeDetail() {
  const reportData = {
    name: 'Work Relationship Decode',
    estimatedTime: '4–5 minutes',
    depth: 'Full diagnostic (L2–L4)',
    price: '₹999',
    analyses: ['Authority and peer dynamic mapping', 'Adaptive response patterns in work relationships', 'Implicit risk perception and constraint loops', 'Outcome patterns and relationship sustainability'],
    receives: [{
      level: 'L2',
      description: 'Work relationship mechanics and response loops'
    }, {
      level: 'L3',
      description: 'Power and peer perception patterns'
    }, {
      level: 'L4',
      description: 'Constraint points and leverage opportunities'
    }, {
      level: 'Optional',
      description: 'Cross-context work relationship implications'
    }],
    doesNotDo: ['Does not evaluate competence or performance', 'Does not assign personality types or labels', 'Does not provide advice or action plans', 'Does not replace coaching or conflict resolution'],
    inputsRequired: {
      baseline: false,
      reportSpecific: 'Work relationship context and dynamic-specific questions',
      estimatedInputTime: '4–5 minutes'
    }
  };
  const handleUnlockReport = () => {
    // Store selected report type (Class B - no baseline required)
    localStorage.setItem('selectedReport', 'work-relationship-decode');
  };
  return <>
      <Helmet data-id="element-4664">
        <title data-id="element-4665">{reportData.name} — Decode | Lazy Monk Labs</title>
        <meta name="description" content={`Full diagnostic report: ${reportData.name}`} data-id="element-4666" />
        <meta name="robots" content="noindex, nofollow" data-id="element-4667" />
        <link rel="canonical" href="https://lazymonklabs.com/work-relationship-decode-detail" data-id="element-4668" />

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-4669"></script>
        <script data-id="element-4670">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4671">
        {/* Navigation */}
        <header data-id="element-4672">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-4673">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4674">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-4675">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4676" />
                <span className="text-lg font-medium tracking-tight" data-id="element-4677">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-4678">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4679">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-4680">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-4681">
          <div className="max-w-3xl mx-auto" data-id="element-4682">
            {/* Page Header */}
            <div className="mb-16" data-id="element-4683">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4684">
                {reportData.name}
              </h1>
              <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-4685">
                Full diagnostic report
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-xs text-[#F5F5F5]/40" data-id="element-4686">
                <span data-id="element-4687">
                  Estimated time to complete: {reportData.estimatedTime}
                </span>
                <span className="hidden sm:inline" data-id="element-4688">·</span>
                <span data-id="element-4689">Report depth: {reportData.depth}</span>
              </div>
            </div>

            {/* Section 1: What This Report Analyzes */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4690">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4691">
                What this report analyzes
              </h2>
              <ul className="space-y-4" data-id="element-4692">
                {reportData.analyses.map((item, index) => <li key={index} className="flex gap-4" data-id="element-4693">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-4694">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-4695">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 2: What You'll Receive */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4696">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4697">
                What you'll receive
              </h2>
              <div className="space-y-4" data-id="element-4698">
                {reportData.receives.map((item, index) => <div key={index} className="flex gap-4" data-id="element-4699">
                    <span className="text-[#F5F5F5]/50 font-medium text-sm" data-id="element-4700">
                      {item.level}
                    </span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-4701">
                      {item.description}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Section 3: What This Report Does Not Do */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4702">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4703">
                What this report does not do
              </h2>
              <ul className="space-y-4" data-id="element-4704">
                {reportData.doesNotDo.map((item, index) => <li key={index} className="flex gap-4" data-id="element-4705">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-4706">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-4707">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 4: Inputs Required */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4708">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4709">
                Inputs required
              </h2>
              <div className="space-y-6" data-id="element-4710">
                {!reportData.inputsRequired.baseline && <div data-id="element-4711">
                    <p className="text-[#F5F5F5]/70 mb-2" data-id="element-4712">
                      No baseline setup required for this report.
                    </p>
                  </div>}
                <div data-id="element-4713">
                  <p className="text-[#F5F5F5]/70 mb-2" data-id="element-4714">
                    {reportData.inputsRequired.reportSpecific}
                  </p>
                </div>
                <div data-id="element-4715">
                  <p className="text-xs text-[#F5F5F5]/50" data-id="element-4716">
                    Estimated input time:{' '}
                    {reportData.inputsRequired.estimatedInputTime}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Price & Purchase */}
            <section className="mb-12" data-id="element-4717">
              <div className="border border-[#2B2B2B] p-8" data-id="element-4718">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-4719">
                  <div data-id="element-4720">
                    <p className="text-3xl font-medium mb-2" data-id="element-4721">
                      {reportData.price}
                    </p>
                    <p className="text-xs text-[#F5F5F5]/50" data-id="element-4722">
                      One-time purchase · No subscription
                    </p>
                  </div>
                  <Link to="/login" onClick={handleUnlockReport} className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg whitespace-nowrap" data-id="element-4723">
                    Unlock Full Report
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-4724">
          <div className="max-w-7xl mx-auto text-center" data-id="element-4725">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-4726">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}